import {
  AutoStoriesOutlined,
  BusinessCenterOutlined,
  ChatBubble,
  HomeOutlined,
  LibraryBooksOutlined,
  PlayCircleOutline,
} from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function ChatBottomNav(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const hasActiveMembership = Boolean(user?.active_membership);

  const isTabActive = (path: string): boolean => {
    const pathname = location.pathname;
    if (path === '/home') {
      return pathname === '/home';
    }
    if (path === '/dashboard') {
      return (
        (pathname === '/dashboard' || pathname.startsWith('/chat')) &&
        !pathname.startsWith('/chat-archive')
      );
    }
    if (path === '/chat-archive') {
      return pathname.startsWith('/chat-archive');
    }
    if (path === '/habits') {
      return pathname.startsWith('/habits');
    }
    if (path === '/content') {
      return pathname.startsWith('/content');
    }
    if (path === '/good-to-go') {
      return pathname.startsWith('/good-to-go');
    }
    return pathname === path;
  };

  const navItems = [
    { icon: HomeOutlined, label: 'Home', path: '/home' },
    { icon: ChatBubble, label: 'Chat', path: '/dashboard' },
    { icon: AutoStoriesOutlined, label: 'Habits', path: '/habits' },
    { icon: LibraryBooksOutlined, label: 'Archive', path: '/chat-archive' },
    { icon: PlayCircleOutline, label: 'Content', path: '/content' },
    ...(hasActiveMembership
      ? [{ icon: BusinessCenterOutlined, label: 'G2G', path: '/good-to-go' }]
      : []),
  ];
  const buttonSize = hasActiveMembership ? 52 : 62;
  const iconSize = hasActiveMembership ? 23 : 26;

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#FFFFFF',
        pt: 1.5,
        pb: 2,
        display: 'block',
        flexShrink: 0,
        zIndex: 10,
        boxShadow: '0px -2px 6px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          px: 2,
        }}
      >
        {navItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = isTabActive(item.path);
          const isG2G = item.path === '/good-to-go';

          let buttonBg = '#AFAFAF';
          let buttonBorder = '1px solid #AFAFAF';
          let iconColor = '#FFFFFF';
          let labelColor = '#828282';
          let fontWeight = 400;

          if (isG2G) {
            if (isActive) {
              buttonBg = '#206B9A';
              buttonBorder = '2px solid #FFD400';
              iconColor = '#FFFFFF';
              labelColor = '#206B9A';
              fontWeight = 700;
            } else {
              buttonBg = '#3A8DBF';
              buttonBorder = '1px solid #3A8DBF';
              iconColor = '#FFFFFF';
              labelColor = '#828282';
            }
          } else if (isActive) {
            buttonBg = '#FFD400';
            buttonBorder = '1px solid #FFD400';
            iconColor = '#000000';
            labelColor = '#000000';
            fontWeight = 700;
          }

          return (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <IconButton
                aria-label={item.label}
                aria-selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  width: 62,
                  height: 57,
                  maxWidth: buttonSize,
                  maxHeight: buttonSize,
                  bgcolor: buttonBg,
                  border: buttonBorder,
                  borderRadius: '32px',
                  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isActive ? 'scale(1.04)' : 'scale(1)',
                  '&:hover': {
                    bgcolor: isG2G
                      ? isActive
                        ? '#1A5A82'
                        : '#2E7AAB'
                      : isActive
                      ? '#E6BF00'
                      : '#9E9E9E',
                    transform: 'scale(1.06)',
                  },
                  '&:active': {
                    transform: 'scale(0.96)',
                  },
                }}
              >
                <IconComponent
                  sx={{
                    color: iconColor,
                    fontSize: item.label === 'Content' ? iconSize + 4 : iconSize,
                    transition: 'color 0.2s ease',
                  }}
                />
              </IconButton>
              <Typography
                sx={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 11,
                  color: labelColor,
                  fontWeight,
                  textAlign: 'center',
                  transition: 'color 0.2s ease',
                }}
              >
                {item.label}
              </Typography>
              {isActive && (
                <Box
                  sx={{
                    width: 16,
                    height: 3,
                    bgcolor: isG2G ? '#206B9A' : '#FFD400',
                    borderRadius: '2px',
                    mt: -0.25,
                    transition: 'all 0.2s ease',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
